import { canViewInLightbox } from '@/components/ui/Lightbox';
import { useAttachmentUrl } from '@/features/attachments/useAttachmentUrl';
import getExtensionFromMimeType from '@/features/attachments/getExtensionFromMimeType';
import downloadAttachment from '@/features/attachments/dowloadAttachment';
import { useIsOnline } from '@/features/offline/useSyncStatus';
import { resolveUrl } from '@/lib/api-utils';
import type { AddAttachmentPayload } from '@certhon/models';
import { FileIcon as LucideFileIcon, Download, ImageOff } from 'lucide-react';
import bytes from 'bytes';
import {
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Shared props for rendering an attachment preview inside a timeline entry.
 * Works with both ticket and checklist events -- callers map their
 * event-specific payload into this shape.
 */
export interface TimelineAttachmentContentProps {
  checksum: string;
  contentType: string;
  size: number;
  filename?: string | null;
  onOpenInLightbox?: (attachment: AddAttachmentPayload) => void;
}

// -- internal helpers --------------------------------------------------------

/** URL of the original file, which the browser shows inline in a new tab. */
function originalUrl(payload: AddAttachmentPayload) {
  return resolveUrl(`/attachments/${payload.checksum}`);
}

/**
 * A plain click runs `onClick`; a modified or middle click, and the
 * browser's context menu, keep the native link behaviour (new tab/window).
 */
function onPlainClick(onClick: () => void) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onClick();
  };
}

interface MediaWrapperProps {
  children: ReactNode;
  href: string;
  onClick?: () => void;
}

function MediaWrapper({ children, href, onClick }: MediaWrapperProps) {
  return (
    <div className="mt-2">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="relative inline-block cursor-pointer overflow-hidden rounded border
          transition-shadow hover:shadow-md"
        onClick={onPlainClick(() => onClick?.())}
      >
        {children}
      </a>
    </div>
  );
}

function ImageAttachment({
  payload,
  url,
  onError,
  onOpenInLightbox,
}: {
  payload: AddAttachmentPayload;
  url: string;
  onError?: () => void;
  onOpenInLightbox?: (attachment: AddAttachmentPayload) => void;
}) {
  // Belt and braces: the native error event can be lost during the initial
  // mount storm (fired while React is mid-render) or when the browser reuses
  // a cached failure without re-firing. A failed image is detectable as
  // complete-with-zero-naturalWidth, so check at commit and on a settle tick.
  const checkFailed = useCallback(
    (el: HTMLImageElement | null) => {
      if (el && el.complete && el.naturalWidth === 0) {
        onError?.();
      }
    },
    [onError],
  );
  const imgRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => checkFailed(imgRef.current), 1500);
    return () => clearTimeout(timer);
  }, [checkFailed, url]);

  return (
    <MediaWrapper
      href={originalUrl(payload)}
      onClick={() => onOpenInLightbox?.(payload)}
    >
      <img
        ref={(el) => {
          imgRef.current = el;
          checkFailed(el);
        }}
        src={url}
        alt={payload.filename || 'Image attachment'}
        className="max-h-64 max-w-xs object-contain"
        loading="lazy"
        onError={onError}
      />
    </MediaWrapper>
  );
}

function VideoAttachment({
  payload,
  url,
  onError,
  onOpenInLightbox,
}: {
  payload: AddAttachmentPayload;
  url: string;
  onError?: () => void;
  onOpenInLightbox?: (attachment: AddAttachmentPayload) => void;
}) {
  return (
    <MediaWrapper
      href={originalUrl(payload)}
      onClick={() => onOpenInLightbox?.(payload)}
    >
      <video
        src={url}
        controls
        preload="metadata"
        className="max-h-64 max-w-xs"
        onError={onError}
      >
        Your browser does not support the video tag.
      </video>
    </MediaWrapper>
  );
}

/**
 * Graceful degradation when media can't load: a quiet card instead of the
 * browser's broken-image glyph. The error event carries no HTTP status, so
 * the cause is inferred from connectivity at error time: offline → "loads
 * when you reconnect", online → unexpected load failure. Resets when the
 * browser reports back online.
 */
function UnavailableAttachment({
  payload,
  offline,
}: {
  payload: AddAttachmentPayload;
  offline: boolean;
}) {
  return (
    <div className="mt-2">
      <div
        className="text-muted-foreground inline-flex items-center gap-3 rounded border
          border-dashed p-3"
      >
        <ImageOff className="h-10 w-10 flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">
            {payload.filename || 'Attachment'}
          </div>
          <div className="text-xs">
            {offline
              ? 'Not available offline — loads when you reconnect'
              : 'Could not load this attachment'}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * File card. With `onOpen` (files the viewer can show, e.g. PDFs) a click
 * opens the viewer and the download icon becomes its own button; without
 * it a click downloads.
 */
function FileAttachment({
  payload,
  extension,
  onDownload,
  onOpen,
}: {
  payload: AddAttachmentPayload;
  extension?: string;
  onDownload: (attachment: AddAttachmentPayload) => void;
  onOpen?: (attachment: AddAttachmentPayload) => void;
}) {
  if (!onOpen) {
    return (
      <div className="mt-2">
        <a
          href={originalUrl(payload)}
          target="_blank"
          rel="noreferrer"
          className="hover:bg-muted/50 inline-flex cursor-pointer items-center gap-3 rounded border
            p-3 transition-colors"
          onClick={onPlainClick(() => onDownload(payload))}
        >
          <FileIconWithExtension extension={extension} />
          <FileDetails payload={payload} />
          <Download className="text-muted-foreground h-4 w-4" />
        </a>
      </div>
    );
  }

  return (
    <div className="mt-2">
      <div className="inline-flex items-center rounded border">
        <a
          href={originalUrl(payload)}
          target="_blank"
          rel="noreferrer"
          className="hover:bg-muted/50 inline-flex min-w-0 cursor-pointer items-center gap-3
            rounded-l p-3 transition-colors"
          onClick={onPlainClick(() => onOpen(payload))}
        >
          <FileIconWithExtension extension={extension} />
          <FileDetails payload={payload} />
        </a>
        <button
          type="button"
          aria-label="Download"
          title="Download"
          className="hover:bg-muted/50 text-muted-foreground self-stretch rounded-r px-3
            transition-colors"
          onClick={() => onDownload(payload)}
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function FileIconWithExtension({ extension }: { extension?: string }) {
  return (
    <div className="relative flex-shrink-0">
      <LucideFileIcon className="text-muted-foreground h-10 w-10" />
      {extension && (
        <div
          className="bg-muted-foreground text-primary-foreground absolute -right-1 -bottom-1 rounded
            px-1 font-mono text-xs"
        >
          {extension.toUpperCase()}
        </div>
      )}
    </div>
  );
}

function FileDetails({ payload }: { payload: AddAttachmentPayload }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="text-foreground truncate text-sm font-medium">
        {payload.filename || 'Unknown file'}
      </div>
      <div className="text-muted-foreground text-xs">
        {bytes(payload.size, { decimalPlaces: 1, unitSeparator: ' ' })}
      </div>
    </div>
  );
}

// -- main export -------------------------------------------------------------

/**
 * Renders a rich inline preview for an attachment inside a timeline entry.
 *
 * - Images: thumbnail with lightbox on click
 * - Videos: inline player with lightbox on click
 * - PDFs: file card that opens the lightbox on click
 * - Other files: file card with download on click
 */
export function TimelineAttachmentContent({
  checksum,
  contentType,
  size,
  filename,
  onOpenInLightbox,
}: TimelineAttachmentContentProps) {
  const payload: AddAttachmentPayload = useMemo(
    () => ({
      checksum,
      contentType,
      size,
      filename: filename ?? undefined,
    }),
    [checksum, contentType, size, filename],
  );

  const [url, punchCache] = useAttachmentUrl(payload);
  const [failedWhile, setFailedWhile] = useState<'online' | 'offline' | null>(
    null,
  );
  const online = useIsOnline();

  // First error punches the (server) cache and retries; a second error means
  // the media genuinely can't be served right now → placeholder, tagged with
  // the connectivity at error time (the error event has no HTTP status).
  // Reconnecting resets offline failures for an automatic retry.
  const handleMediaError = useMemo(() => {
    const punched = punchCache(() =>
      setFailedWhile(navigator.onLine ? 'online' : 'offline'),
    );
    return () => punched(new Error('media failed to load'));
  }, [punchCache]);
  useEffect(() => {
    if (online) {
      setFailedWhile((cause) => (cause === 'offline' ? null : cause));
    }
  }, [online]);

  const handleDownload = useCallback((attachment: AddAttachmentPayload) => {
    void downloadAttachment(attachment);
  }, []);

  const isMedia =
    contentType.startsWith('image/') || contentType.startsWith('video/');
  if (isMedia && failedWhile !== null) {
    return (
      <UnavailableAttachment
        payload={payload}
        offline={failedWhile === 'offline'}
      />
    );
  }

  if (contentType.startsWith('image/')) {
    return (
      <ImageAttachment
        payload={payload}
        url={url}
        onError={handleMediaError}
        onOpenInLightbox={onOpenInLightbox}
      />
    );
  }

  if (contentType.startsWith('video/')) {
    return (
      <VideoAttachment
        payload={payload}
        url={url}
        onError={handleMediaError}
        onOpenInLightbox={onOpenInLightbox}
      />
    );
  }

  const extension =
    filename?.split('.').pop() || getExtensionFromMimeType(contentType);

  return (
    <FileAttachment
      payload={payload}
      extension={extension}
      onDownload={handleDownload}
      onOpen={canViewInLightbox(contentType) ? onOpenInLightbox : undefined}
    />
  );
}
